/**
 * XSalsa20-Poly1305 authenticated encryption (NaCl `secretbox`), pure-Kotlin and platform-independent.
 *
 * Ported faithfully from TweetNaCl so the output is byte-compatible with libsodium / tweetnacl-js `secretbox`:
 * [seal] of an N-byte message returns a `(TAG_SIZE + N)`-byte array of `Poly1305 tag || ciphertext`, and [open]
 * reverses it, returning null on any authentication failure.
 *
 * Keeping this in commonMain is the Milestone-13 "approach 甲" decision (roadmap §20.2): the confidentiality cipher
 * lives **once**, so JVM and JS never diverge and cross-platform interop is guaranteed by construction. This is the
 * irreducible cipher core behind the pluggable seal/`combine` boundary (§20.2 layer-4); the GraphV4-native
 * resolution-gating that turns it into "dark, holdable content" lives a layer up in the event module. The
 * wrap-to-identity X25519 layer (§20.3) is added on top in Phase 2.
 */
package com.oasisfeng.invaluable.identity

object SecretBox {

    const val KEY_SIZE = 32
    const val NONCE_SIZE = 24
    const val TAG_SIZE = 16

    /** Authenticated-encrypt [message] under [key] (32 bytes) and [nonce] (24 bytes); returns `tag(16) || ciphertext`. */
    fun seal(key: ByteArray, nonce: ByteArray, message: ByteArray): ByteArray {
        require(key.size == KEY_SIZE) { "Key must be $KEY_SIZE bytes." }
        require(nonce.size == NONCE_SIZE) { "Nonce must be $NONCE_SIZE bytes." }
        // NaCl convention: prepend 32 zero bytes to the message, XOR the whole buffer with the keystream, then the
        // first 32 ciphertext bytes are the Poly1305 auth key and the tag lands in bytes 16..32. The wire form drops
        // the 16 leading (now-zero) bytes, yielding tag || ciphertext (identical to tweetnacl-js secretbox).
        val padded = ByteArray(32 + message.size)
        message.copyInto(padded, destinationOffset = 32)
        val c = ByteArray(padded.size)
        streamXor(c, padded, nonce, key)
        val out = ByteArray(TAG_SIZE + message.size)
        onetimeauth(out, 0, c, 32, c.size - 32, c, 0)   // auth key = c[0..32] (the first keystream block)
        c.copyInto(out, TAG_SIZE, startIndex = 32)
        return out
    }

    /** Verify-and-decrypt a `tag(16) || ciphertext` [boxed] under [key]/[nonce]; null if authentication fails. */
    fun open(key: ByteArray, nonce: ByteArray, boxed: ByteArray): ByteArray? {
        require(key.size == KEY_SIZE) { "Key must be $KEY_SIZE bytes." }
        require(nonce.size == NONCE_SIZE) { "Nonce must be $NONCE_SIZE bytes." }
        if (boxed.size < TAG_SIZE) return null
        val messageSize = boxed.size - TAG_SIZE
        val c = ByteArray(32 + messageSize)              // [16 zeros || tag || ciphertext]
        boxed.copyInto(c, destinationOffset = 16)
        val authKey = ByteArray(32)                      // first keystream block = Poly1305 key
        streamXor(authKey, ByteArray(32), nonce, key)
        val tag = ByteArray(TAG_SIZE)
        onetimeauth(tag, 0, c, 32, messageSize, authKey, 0)
        if (!verify16(tag, 0, c, 16)) return null
        val m = ByteArray(c.size)
        streamXor(m, c, nonce, key)
        return m.copyOfRange(32, 32 + messageSize)
    }

    // --- Salsa20 / XSalsa20 ---------------------------------------------------------------------------------------

    private val SIGMA = "expand 32-byte k".encodeToByteArray()

    private fun ld32(x: ByteArray, off: Int): Int {
        var u = x[off + 3].toInt() and 0xFF
        u = (u shl 8) or (x[off + 2].toInt() and 0xFF)
        u = (u shl 8) or (x[off + 1].toInt() and 0xFF)
        return (u shl 8) or (x[off].toInt() and 0xFF)
    }

    private fun st32(x: ByteArray, off: Int, value: Int) {
        var u = value
        for (i in 0 until 4) { x[off + i] = (u and 0xFF).toByte(); u = u ushr 8 }
    }

    private fun rotl(x: Int, c: Int): Int = (x shl c) or (x ushr (32 - c))

    /** Salsa20 core (`hsalsa = false`) or HSalsa20 core (`hsalsa = true`); [inb] is 16 bytes, [key] 32, [out] 64/32. */
    private fun core(out: ByteArray, inb: ByteArray, inOff: Int, key: ByteArray, hsalsa: Boolean) {
        val x = IntArray(16); val y = IntArray(16); val w = IntArray(16); val t = IntArray(4)
        for (i in 0 until 4) {
            x[5 * i] = ld32(SIGMA, 4 * i)
            x[1 + i] = ld32(key, 4 * i)
            x[6 + i] = ld32(inb, inOff + 4 * i)
            x[11 + i] = ld32(key, 16 + 4 * i)
        }
        for (i in 0 until 16) y[i] = x[i]
        repeat(20) {
            for (j in 0 until 4) {
                for (m in 0 until 4) t[m] = x[(5 * j + 4 * m) % 16]
                t[1] = t[1] xor rotl(t[0] + t[3], 7)
                t[2] = t[2] xor rotl(t[1] + t[0], 9)
                t[3] = t[3] xor rotl(t[2] + t[1], 13)
                t[0] = t[0] xor rotl(t[3] + t[2], 18)
                for (m in 0 until 4) w[4 * j + (j + m) % 4] = t[m]
            }
            for (m in 0 until 16) x[m] = w[m]
        }
        if (hsalsa) {
            for (i in 0 until 16) x[i] = x[i] + y[i]
            for (i in 0 until 4) {
                x[5 * i] = x[5 * i] - ld32(SIGMA, 4 * i)
                x[6 + i] = x[6 + i] - ld32(inb, inOff + 4 * i)
            }
            for (i in 0 until 4) { st32(out, 4 * i, x[5 * i]); st32(out, 16 + 4 * i, x[6 + i]) }
        } else {
            for (i in 0 until 16) st32(out, 4 * i, x[i] + y[i])
        }
    }

    /**
     * HSalsa20 of a 32-byte input [point] under the σ constant with a zero 16-byte input — i.e. NaCl
     * `crypto_box_beforenm`'s key-derivation step. Shared with [IdentityBox] so the X25519 `box` layer reuses this
     * one HSalsa20 core instead of re-porting it (approach 甲: the cipher lives once).
     */
    internal fun beforeNmKey(point: ByteArray): ByteArray {
        val k = ByteArray(32)
        core(k, ByteArray(16), 0, point, hsalsa = true)
        return k
    }

    /** XSalsa20 keystream XOR: HSalsa20 derives a subkey from the first 16 nonce bytes, then Salsa20 with the last 8. */
    private fun streamXor(c: ByteArray, m: ByteArray, nonce: ByteArray, key: ByteArray) {
        val subkey = ByteArray(32)
        core(subkey, nonce, 0, key, hsalsa = true)
        val z = ByteArray(16)
        for (i in 0 until 8) z[i] = nonce[16 + i]
        val block = ByteArray(64)
        var b = m.size
        var off = 0
        while (b >= 64) {
            core(block, z, 0, subkey, hsalsa = false)
            for (i in 0 until 64) c[off + i] = (m[off + i].toInt() xor block[i].toInt()).toByte()
            var u = 1
            for (i in 8 until 16) { u += z[i].toInt() and 0xFF; z[i] = (u and 0xFF).toByte(); u = u ushr 8 }
            b -= 64; off += 64
        }
        if (b > 0) {
            core(block, z, 0, subkey, hsalsa = false)
            for (i in 0 until b) c[off + i] = (m[off + i].toInt() xor block[i].toInt()).toByte()
        }
    }

    // --- Poly1305 -------------------------------------------------------------------------------------------------

    private val MINUSP = longArrayOf(5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 252)

    private fun add1305(h: LongArray, c: LongArray) {
        var u = 0L
        for (j in 0 until 17) { u += h[j] + c[j]; h[j] = u and 255; u = u shr 8 }
    }

    /** Poly1305 MAC of `m[mOff until mOff+mLen]` keyed by `key[keyOff until keyOff+32]`, written to `out[outOff..+16]`. */
    private fun onetimeauth(out: ByteArray, outOff: Int, m: ByteArray, mOff: Int, mLen: Int, key: ByteArray, keyOff: Int) {
        val r = LongArray(17); val h = LongArray(17); val c = LongArray(17); val g = LongArray(17); val x = LongArray(17)
        for (j in 0 until 16) r[j] = (key[keyOff + j].toInt() and 0xFF).toLong()
        r[3] = r[3] and 15; r[4] = r[4] and 252; r[7] = r[7] and 15; r[8] = r[8] and 252
        r[11] = r[11] and 15; r[12] = r[12] and 252; r[15] = r[15] and 15
        var n = mLen
        var pos = mOff
        while (n > 0) {
            for (j in 0 until 17) c[j] = 0
            var j = 0
            while (j < 16 && j < n) { c[j] = (m[pos + j].toInt() and 0xFF).toLong(); j++ }
            c[j] = 1
            pos += j; n -= j
            add1305(h, c)
            for (i in 0 until 17) {
                x[i] = 0
                for (jj in 0 until 17) x[i] += h[jj] * (if (jj <= i) r[i - jj] else 320L * r[i + 17 - jj])
            }
            for (i in 0 until 17) h[i] = x[i]
            var u = 0L
            for (jj in 0 until 16) { u += h[jj]; h[jj] = u and 255; u = u shr 8 }
            u += h[16]; h[16] = u and 3; u = 5 * (u shr 2)
            for (jj in 0 until 16) { u += h[jj]; h[jj] = u and 255; u = u shr 8 }
            u += h[16]; h[16] = u
        }
        for (j in 0 until 17) g[j] = h[j]
        add1305(h, MINUSP)
        val s = -(h[16] shr 7)                           // 0 or -1: constant-time conditional subtract of p
        for (j in 0 until 17) h[j] = h[j] xor (s and (g[j] xor h[j]))
        for (j in 0 until 16) c[j] = (key[keyOff + 16 + j].toInt() and 0xFF).toLong()
        c[16] = 0
        add1305(h, c)
        for (j in 0 until 16) out[outOff + j] = (h[j] and 0xFF).toByte()
    }

    /** Constant-time 16-byte comparison. */
    private fun verify16(a: ByteArray, aOff: Int, b: ByteArray, bOff: Int): Boolean {
        var d = 0
        for (i in 0 until 16) d = d or ((a[aOff + i].toInt() and 0xFF) xor (b[bOff + i].toInt() and 0xFF))
        return d == 0
    }
}
